import axios from 'axios';

const CRM_API_BASE_URL = 'https://crm.bookingwhizz.com/api';

/**
 * Loyalty API Service
 * Handles loyalty-related API calls
 */

/**
 * Fetch redeem override rules
 * @param {Object} params - Request parameters
 * @param {string|number} params.accommodationId - Hotel/Accommodation ID
 * @param {string|number} params.profileId - Profile ID (MasterProfileId or CrmProfileId from logged-in user)
 * @returns {Promise<Object>} Response with redeem override rules
 */
export const fetchRedeemOverrideRules = async (params) => {
  try {
    const { accommodationId, profileId } = params;

    if (!accommodationId) {
      throw new Error('Accommodation ID is required');
    }

    if (!profileId) {
      throw new Error('Profile ID is required');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('accommodationId', accommodationId.toString());
    queryParams.append('profileId', profileId.toString());

    const response = await axios.get(
      `${CRM_API_BASE_URL}/loyalty/fetch-redeem-override-rules?${queryParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'user-id': 'crs_api',
          'password': 'crs7c487$k'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch redeem override rules');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred while fetching redeem override rules');
    }
  }
};

/**
 * Get available loyalty points for a user
 * @param {Object} params - Request parameters
 * @param {string|number} params.crmProfileId - CRM Profile ID (CrmProfileId from logged-in user)
 * @param {string|number} [params.categoryId=2] - Category ID (default: 2)
 * @returns {Promise<Object>} Response with available points
 */
export const getAvailablePoints = async (params) => {
  try {
    const { crmProfileId, categoryId = 2 } = params;

    if (!crmProfileId) {
      throw new Error('CRM Profile ID is required');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('categoryId', categoryId.toString());

    const url = `${CRM_API_BASE_URL}/loyalty/get-available-points/${crmProfileId}?${queryParams.toString()}`;

    console.log('====== 💰 GET AVAILABLE POINTS API ======');
    console.log('🚀 Hitting URL:', url);
    console.log('📦 Params:', { crmProfileId, categoryId });
    console.log('==========================================');

    const response = await axios.get(
      url,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'user-id': 'crs_api',
          'password': 'crs7c487$k'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    console.log('====== 💰 GET AVAILABLE POINTS RESPONSE ======');
    console.log('✅ Status:', response.status);
    console.log('📥 Response Data:', response.data);
    console.log('💵 Points:', response.data?.total?.points || 0);
    console.log('==============================================');

    return response.data;
  } catch (error) {
    console.error('====== ❌ GET AVAILABLE POINTS ERROR ======');
    console.error('Status:', error.response?.status);
    console.error('Error Data:', error.response?.data);
    console.error('Message:', error.message);
    console.error('============================================');

    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to get available points');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred while getting available points');
    }
  }
};

/**
 * Request loyalty points for accommodation
 * @param {Object} params - Request parameters
 * @param {string|number} params.accommodationId - Accommodation ID (from URL, e.g., 100418)
 * @param {string|number} params.hotelId - Hotel ID / BE Accommodation ID (beAccId, e.g., 2000)
 * @returns {Promise<Object>} Response with loyalty points request
 */
export const requestLoyaltyPointsForAccommodation = async (params) => {
  try {
    const { accommodationId, hotelId } = params;

    if (!accommodationId) {
      throw new Error('Accommodation ID is required');
    }

    if (!hotelId) {
      throw new Error('Hotel ID is required');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('beAccId', hotelId.toString());

    const response = await axios.get(
      `${CRM_API_BASE_URL}/loyalty/request-loyalty-points-for-accommodation/${accommodationId}?${queryParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'user-id': 'crs_api',
          'password': 'crs7c487$k'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to request loyalty points for accommodation');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred while requesting loyalty points for accommodation');
    }
  }
};

/**
 * Check redemption eligibility for loyalty points
 * @param {Object} params - Request parameters
 * @param {string|number} params.profileId - CRM Profile ID (CrmProfileId from logged-in user)
 * @param {string|number} params.transactionAmount - Transaction amount
 * @param {string|number} params.redeemPoints - Points to redeem
 * @param {string} params.currencyCode - Currency code (e.g., 'INR', 'USD')
 * @param {string|number} params.accommodationId - Hotel/Accommodation ID
 * @param {string|number} [params.categoryId=2] - Category ID (default: 2)
 * @param {string|number} [params.referenceNo=0] - Reference number (default: 0)
 * @returns {Promise<Object>} Response with redemption check result
 */
export const checkRedemption = async (params) => {
  try {
    const { 
      profileId, 
      transactionAmount, 
      redeemPoints, 
      currencyCode, 
      accommodationId,
      categoryId = 2,
      referenceNo = 0
    } = params;

    if (!profileId) {
      throw new Error('Profile ID is required');
    }

    if (!transactionAmount) {
      throw new Error('Transaction amount is required');
    }

    if (!redeemPoints) {
      throw new Error('Redeem points is required');
    }

    if (!currencyCode) {
      throw new Error('Currency code is required');
    }

    if (!accommodationId) {
      throw new Error('Accommodation ID is required');
    }

    const requestData = {
      profileId: profileId.toString(),
      categoryId: categoryId.toString(),
      transactionAmount: transactionAmount.toString(),
      points: redeemPoints.toString(),
      referenceNo: referenceNo.toString(),
      currencyCode: currencyCode.toString(),
      accommodationId: accommodationId.toString()
    };

    const url = `${CRM_API_BASE_URL}/loyalty/check-redemption`;

    console.log('====== 🎯 CHECK REDEMPTION API ======');
    console.log('🚀 Hitting URL:', url);
    console.log('📦 Request Body:', requestData);
    console.log('======================================');

    const response = await axios.post(
      url,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'user-id': 'crs_api',
          'password': 'crs7c487$k'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    console.log('====== 🎯 CHECK REDEMPTION RESPONSE ======');
    console.log('✅ Status:', response.status);
    console.log('📥 Response Data:', response.data);
    console.log('💵 Amount Value:', response.data?.amountValue);
    console.log('==========================================');

    return response.data;
  } catch (error) {
    console.error('====== ❌ CHECK REDEMPTION ERROR ======');
    console.error('Status:', error.response?.status);
    console.error('Error Data:', error.response?.data);
    console.error('Message:', error.message);
    console.error('========================================');

    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to check redemption');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred while checking redemption');
    }
  }
};
